// Loaders for every in-app page (#12239).
//
// Each page is its own chunk, fetched the first time its route renders, so the
// entry script carries only the app shell. MainApp builds its `React.lazy`
// components from this map, and `routeModules.test.ts` awaits every entry, so
// a wrong path or a named export read as a default fails a test rather than
// the page a user opens.
//
// Import each page's own file. Never the `FpPages` barrel: it re-exports every
// page, so one import of it would pull all of them back into a single chunk.
// A page with a named export is mapped to `default` here, which is the shape
// `React.lazy` requires.
export const routeModules = {
  // Staff admin (/__admin/*)
  AdminControl: () => import("../AdminPages/AdminControl"),
  AdminDashboardTemplateEditor: () =>
    import("../AdminPages/AdminDashboardTemplateEditor"),
  ManagePromptTemplates: () => import("../AdminPages/PromptTemplates"),

  // Review (/review/*)
  FilesListView: () => import("../FpFundBrain/Review/FilesListView"),
  ReviewView: () => import("../FpFundBrain/Review/ReviewView"),
  CheckReviewView: () => import("../FpFundBrain/Review/CheckReviewView"),

  // Highlighter
  FpHighlighterWithURLParams: () =>
    import("../common/FpHighlighter").then((m) => ({
      default: m.FpHighlighterWithURLParams,
    })),
  FpHighlighterWithUUID: () =>
    import("../common/FpHighlighter").then((m) => ({
      default: m.FpHighlighterWithUUID,
    })),

  // Home, alerts, reports, artifacts
  HomePage: () => import("../FpPages/Home"),
  // The Alerts folder's index re-exports alert components only.
  AlertsPage: () => import("../FpPages/Alerts"),
  AlertEditor: () => import("../FpPages/Alerts/AlertEditor"),
  ReportsPage: () => import("../FpPages/Reports"),
  ReportEditor: () => import("../FpPages/Reports/ReportEditor"),
  ReportTemplateEditor: () => import("../FpPages/Reports/ReportTemplateEditor"),
  ArtifactsPage: () => import("../FpPages/Artifacts/ArtifactsPage"),

  // Chat, skills, tasks, agents
  ChatConversation: () => import("../FpPages/Chat/ChatConversation"),
  ChatsHub: () => import("../FpPages/Chat/ChatsHub"),
  SkillsPage: () => import("../FpPages/Skills/SkillsPage"),
  SkillDetail: () => import("../FpPages/Skills/SkillDetail"),
  TasksPage: () => import("../FpPages/Tasks/TasksPage"),
  TaskListPage: () => import("../FpPages/Tasks/TaskListPage"),
  AgentsPage: () => import("../FpPages/Agents/AgentsPage"),
  AgentDetail: () => import("../FpPages/Agents/AgentDetail"),
  AgentRunView: () => import("../FpPages/Agents/AgentRunView"),
  AgentChatConversation: () =>
    import("../FpPages/Agents/AgentChatConversation"),

  // Documents, claims intake, exposures
  DocumentsPage: () => import("../FpPages/Documents"),
  IntakePage: () => import("../FpPages/Intake/IntakePage"),
  ClaimsReviewPage: () => import("../FpPages/Intake/ClaimsReviewPage"),
  Exposures: () => import("../FpFundBrain/Exposures"),

  // Manage (legacy) and Settings
  Manage: () => import("../FpFundBrain/Manage"),
  Emails: () => import("../FpFundBrain/Manage/Emails"),
  ManageUsers: () => import("../FpFundBrain/Manage/Users"),
  FundPortfolios: () => import("../FpFundBrain/Manage/FundPortfolios"),
  ManageConnections: () => import("../FpFundBrain/Manage/Connections"),
  SettingsPage: () => import("../FpPages/Settings"),

  // Matrix
  MatrixPage: () => import("../FpPages/Matrix"),
  MatrixEditor: () => import("../FpPages/Matrix/MatrixEditor"),
  MatrixTemplateEditor: () =>
    import("../FpPages/MatrixTemplates/MatrixTemplateEditor"),

  // RMS
  RMSPage: () => import("../FpPages/RMS"),
  SetupPage: () => import("../FpPages/RMS/Setup/SetupPage"),
  InteractionPage: () => import("../FpPages/RMS/Pages/InteractionPage"),
  EntityDashboard: () => import("../FpPages/RMS/Dashboard/EntityDashboard"),
  ComponentBuilderRoute: () =>
    import("../FpPages/RMS/Components/ComponentBuilderRoute").then((m) => ({
      default: m.ComponentBuilderRoute,
    })),

  // Notetaker
  NotetakerIndexPage: () => import("../FpPages/Notetaker/NotetakerIndexPage"),
  NotetakerInstallWalkthroughPage: () =>
    import("../FpPages/Notetaker/NotetakerInstallWalkthroughPage"),
  NotetakerMeetingPage: () =>
    import("../FpPages/Notetaker/NotetakerMeetingPage"),
  NotetakerChatHistoryPage: () =>
    import("../FpPages/Notetaker/NotetakerChatHistoryPage"),
  NotetakerChatViewPage: () =>
    import("../FpPages/Notetaker/NotetakerChatViewPage"),

  // Not a page: the app-wide task drawer. It imports from the Tasks page, so
  // loading it eagerly would put the whole Tasks page in the entry chunk. It
  // only opens on a user action, long after its chunk has arrived.
  TaskOpenBridge: () => import("../FpPages/Tasks/TaskOpenBridge"),

  // Not a page either: prefetches RMS Home once the app is idle, on every page (#11849). Lazy,
  // so the RMS modules it needs stay out of the entry chunk.
  RmsHomePrefetch: () => import("../FpPages/RMS/Components/RmsHomePrefetch"),

  // CRM (legacy)
  CRMPage: () => import("../FpPages/CRM"),
  AssetSummaryWrapper: () =>
    import("../FpPages/CRM/assets/AssetSummaryWrapper"),
  AssetMeetingsNotesWrapper: () =>
    import("../FpPages/CRM/assets/AssetMeetingsNotesWrapper"),
  ManagerView: () => import("../FpPages/CRM/managers/ManagerViewWrapper"),
  ManagerMeetingsNotes: () =>
    import("../FpPages/CRM/managers/ManagerMeetingsNotesWrapper"),
  ReportsWrapper: () => import("../FpPages/CRM/reports/ReportsWrapper"),
};
